import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Title, Text, Group, Stack, Button, ActionIcon, Badge, Box, Modal } from '@mantine/core';
import { IconCopy, IconX, IconPlayerPlay, IconPencil } from '@tabler/icons-react';
import { apiClient } from '../services/api';
import { Game, Player } from '../types/models';
import useClipboard from '../hooks/useClipboard';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { MobileLayout } from './layouts/MobileLayout';
import { useViewportSize } from '../hooks/useViewportSize';
import { LobbyPlayerPolaroid } from './LobbyPlayerPolaroid';
import PushNotificationPrompt from './PushNotificationPrompt';

interface GameLobbyProps {
  game: Game;
  currentPlayer: Player;
}

const GameLobby: React.FC<GameLobbyProps> = ({ game, currentPlayer }) => {
  const players = game.players || [];
  const { isMobile, isTablet } = useResponsiveLayout();
  const viewport = useViewportSize();
  const [userHandwriting, setUserHandwriting] = useState<string>(() => {
    const saved = localStorage.getItem('userHandwriting');
    if (saved) return saved;
    // Pick a random font for new users
    const fonts = [
      'Permanent Marker', 'Caveat', 'Shadows Into Light', 'Indie Flower',
      'Gloria Hallelujah', 'Amatic SC', 'Rock Salt', 'Nothing You Could Do',
      'Covered By Your Grace', 'Just Another Hand', 'La Belle Aurore',
      'Cedarville Cursive', 'Waiting for the Sunrise'
    ];
    const randomFont = fonts[Math.floor(Math.random() * fonts.length)];
    localStorage.setItem('userHandwriting', randomFont);
    return randomFont;
  });
  // Calculate optimal polaroid scale
  const calculatePolaroidScale = () => {
    
    const maxScale = 2.5;
    const minScale = 1;
    const baseSize = 140; // Base polaroid width including gaps
    const padding = 40; // Total horizontal padding
    const gap = 32; // Gap between polaroids (xl gap)
    
    // Use full viewport width minus padding
    const containerWidth = viewport.width - padding;
    
    // Determine optimal number of columns based on viewport
    let columns = 4;
    if (viewport.width < 800) columns = 2;
    else if (viewport.width < 1000) columns = 3;
    
    // Don't use more columns than we have players
    columns = Math.min(columns, players.length);
    
    // Calculate available width per polaroid
    const totalGapWidth = (columns - 1) * gap;
    const availableWidthPerPolaroid = (containerWidth - totalGapWidth) / columns;
    
    // Calculate scale to fit available space
    const scale = Math.min(maxScale, Math.max(minScale, availableWidthPerPolaroid / 120));
    
    return scale;
  };
  
  const polaroidScale = calculatePolaroidScale();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removingPlayerId, setRemovingPlayerId] = useState<number | null>(null);
  const [confirmRemovePlayer, setConfirmRemovePlayer] = useState<Player | null>(null);
  const navigate = useNavigate();
  const copy = useClipboard();

  const minPlayers = 3;
  const hasEnoughPlayers = players.length >= minPlayers;
  const hasHost = players.some(p => p.isHost);
  const canStartGame = hasEnoughPlayers && hasHost;
  const playersNeeded = minPlayers - players.length;

  const handleStartGame = async () => {
    if (!currentPlayer.isHost || !canStartGame) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      await apiClient.startGame(game.key);
    } catch (err: any) {
      console.error('Failed to start game:', err);
      setError(err.error || 'Failed to start game');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemovePlayerClick = (player: Player) => {
    if (!currentPlayer.isHost) return;
    setConfirmRemovePlayer(player);
  };

  const handleConfirmRemovePlayer = async () => {
    if (!confirmRemovePlayer || !currentPlayer.isHost) return;
    
    setRemovingPlayerId(Number(confirmRemovePlayer.id));
    setError(null);
    
    try {
      await apiClient.removePlayer(game.key, Number(confirmRemovePlayer.id));
      setConfirmRemovePlayer(null);
    } catch (err: any) {
      console.error('Failed to remove player:', err);
      if (err.error) {
        setError(err.error);
      } else {
        setError('Failed to remove player. They will be kicked back to the home page if the removal succeeds.');
      }
    } finally {
      setRemovingPlayerId(null);
    }
  };

  const handleCancelRemovePlayer = () => {
    setConfirmRemovePlayer(null);
  };

  const handleCopyInvite = () => {
    const url = `${window.location.origin}/games/${game.key}`;
    copy(url);
  };

  const handleEditAvatar = () => {
    navigate(`/games/${game.key}/avatar`);
  };

  const handleFontChange = async (newFont: string) => {
    setUserHandwriting(newFont);
    localStorage.setItem('userHandwriting', newFont);
    
    // Update the player's handwriting font on the server
    try {
      await apiClient.updatePlayer(game.key, currentPlayer.id, {
        handwritingFont: newFont
      });
    } catch (err) {
      console.error('Failed to update handwriting font:', err);
    }
  };



  const content = (
    <Stack gap="xl" align="center" style={{ width: '100%' }}>
      {/* Header */}
      <Stack gap="sm" align="center">
        <Title order={isMobile ? 2 : 1} ta="center">
          {game.players.find(p => p.isHost)?.name || 'Someone'}'s Game
        </Title>
        <Group gap="sm" justify="center">
          <Button
            variant="light"
            leftSection={<IconCopy size={16} />}
            onClick={handleCopyInvite}
            size={isMobile ? "md" : "sm"}
          >
            Copy Invite Link
          </Button>
          <Button
            variant="light"
            leftSection={<IconPencil size={16} />}
            onClick={handleEditAvatar}
            size={isMobile ? "md" : "sm"}
          >
            Edit Avatar
          </Button>
          {currentPlayer.isHost && (
            <Button
              size={isMobile ? "md" : "sm"}
              leftSection={<IconPlayerPlay size={16} />}
              onClick={handleStartGame}
              loading={isLoading}
              disabled={!canStartGame}
            >
              {isLoading ? 'Starting...' : 'Start Game'}
            </Button>
          )}
        </Group>
      </Stack>


      {/* Player List - Always polaroids */}
      <Group justify="center" gap={polaroidScale > 1.5 ? "xl" : "lg"} w="100%">
        {players.map((player) => (
          <Box key={player.id} pos="relative">
            <LobbyPlayerPolaroid
              avatar={player.avatarUrl}
              name={player.name}
              scale={polaroidScale}
              handwritingFont={player.id === currentPlayer.id ? userHandwriting : player.handwritingFont}
              isCurrentPlayer={player.id === currentPlayer.id}
              onFontChange={player.id === currentPlayer.id ? handleFontChange : undefined}
            />
            
            {currentPlayer.isHost && player.id !== currentPlayer.id && (
              <ActionIcon
                variant="light"
                color="red"
                size="xs"
                onClick={() => handleRemovePlayerClick(player)}
                loading={removingPlayerId === Number(player.id)}
                title="Remove player"
                style={{ 
                  position: 'absolute', 
                  top: 4 * polaroidScale, 
                  right: 4 * polaroidScale 
                }}
              >
                <IconX size={12} />
              </ActionIcon>
            )}
          </Box>
        ))}
      </Group>

      {/* Error Message */}
      {error && (
        <Text c="red" ta="center">
          {error}
        </Text>
      )}

      {/* Turn notifications */}
      <PushNotificationPrompt gameKey={game.key} />

      {/* Game Status */}
      <Box w={isMobile ? "100%" : "auto"}>
        {currentPlayer.isHost ? (
          !canStartGame && playersNeeded > 0 && (
            <Text size="sm" c="dimmed" ta="center">
              Need {playersNeeded} more {playersNeeded === 1 ? 'player' : 'players'}
            </Text>
          )
        ) : (
          <Stack gap="md" align="center">
            <Text c="dimmed" ta="center">
              Waiting for {game.players.find(p => p.isHost)?.name || 'the host'} to start the game
            </Text>
            {playersNeeded > 0 && (
              <Badge variant="light" size={isMobile ? "md" : "lg"}>
                Need {playersNeeded} more {playersNeeded === 1 ? 'player' : 'players'}
              </Badge>
            )}
          </Stack>
        )}
      </Box>
      
    </Stack>
  );

  if (isMobile) {
    return (
      <MobileLayout fullHeight safeArea>
        <Box p="md" style={{ height: '100%', overflowY: 'auto' }}>
          {content}
        </Box>
        
        {/* Confirmation Modal */}
        <Modal
          opened={!!confirmRemovePlayer}
          onClose={handleCancelRemovePlayer}
          title="Remove Player"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to remove <strong>{confirmRemovePlayer?.name}</strong> from the game?
            </Text>
            <Text size="sm" c="dimmed">
              They will be kicked back to the home page but can rejoin with the invite link.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                onClick={handleCancelRemovePlayer}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleConfirmRemovePlayer}
                loading={removingPlayerId === Number(confirmRemovePlayer?.id)}
              >
                Remove
              </Button>
            </Group>
          </Stack>
        </Modal>
      </MobileLayout>
    );
  }

  // Desktop layout
  return (
    <Box style={{ minHeight: '100vh' }}>
      <Container size="md" py="xl">
        {content}
        
        {/* Confirmation Modal */}
        <Modal
          opened={!!confirmRemovePlayer}
          onClose={handleCancelRemovePlayer}
          title="Remove Player"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to remove <strong>{confirmRemovePlayer?.name}</strong> from the game?
            </Text>
            <Text size="sm" c="dimmed">
              They will be kicked back to the home page but can rejoin with the invite link if they have it.
            </Text>
            <Text size="xs" c="orange">
              Note: Players can only be removed if they haven't made any entries yet.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                onClick={handleCancelRemovePlayer}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleConfirmRemovePlayer}
                loading={removingPlayerId === Number(confirmRemovePlayer?.id)}
              >
                Remove Player
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </Box>
  );
};

export default GameLobby; 
